<template>
  <div class="agent-manager">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h2 class="text-lg font-bold">智能体管理</h2>
        <p class="text-xs text-neutral-500 mt-1">
          配置后，Web 端与小程序端都能直接在站内对话使用，无需跳转外部平台
        </p>
      </div>
      <button
        @click="openEdit(null)"
        class="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        <i class="fa fa-plus mr-1"></i>新增智能体
      </button>
    </div>

    <div v-if="loading" class="text-center py-10">
      <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
    </div>

    <div v-else-if="list.length === 0" class="text-center py-16 border rounded-xl">
      <p class="text-neutral-500">还没有智能体，点右上角新增</p>
    </div>

    <div
      v-for="(a, idx) in list"
      :key="a.id"
      class="border border-neutral-200 rounded-xl p-4 mb-3 flex items-start gap-4 bg-white"
    >
      <div
        class="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-blue-400 flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden"
      >
        <img v-if="a.avatar" :src="a.avatar" class="w-full h-full object-cover" />
        <span v-else>{{ a.name ? a.name.charAt(0) : "A" }}</span>
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="font-medium">{{ a.name }}</span>
          <span
            class="px-2 py-0.5 rounded text-xs"
            :class="
              a.status === 1
                ? 'bg-green-50 text-green-700'
                : 'bg-neutral-100 text-neutral-500'
            "
            >{{ a.status === 1 ? "启用" : "停用" }}</span
          >
          <span class="px-2 py-0.5 rounded text-xs" :class="examClass(a)">{{
            examText(a)
          }}</span>
          <span
            v-if="a.creatorUid"
            class="px-2 py-0.5 rounded text-xs bg-neutral-100 text-neutral-500"
            >用户投稿</span
          >
          <span class="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">{{
            platformName(a.platform)
          }}</span>
          <span
            class="px-2 py-0.5 rounded text-xs"
            :class="
              a.chatMode === 'api'
                ? 'bg-neutral-100 text-neutral-600'
                : 'bg-purple-50 text-purple-700'
            "
            >{{ a.chatMode === "api" ? "站内代理" : "网页嵌入" }}</span
          >
          <span class="text-xs text-neutral-400"
            >{{ a.useCount || 0 }} 次对话</span
          >
        </div>
        <p class="text-sm text-neutral-500 mt-1 line-clamp-1">
          {{ a.intro || "暂无介绍" }}
        </p>
        <p class="text-xs text-neutral-400 mt-1 truncate">
          {{ a.platform === "openai" ? "模型" : "Bot ID" }}：{{ a.botId || "未填写" }}
          <span v-if="a.apiToken" class="ml-2"
            >密钥：{{ maskToken(a.apiToken) }}</span
          >
          <span v-else class="ml-2 text-amber-600">密钥未配置</span>
        </p>
      </div>

      <div class="flex items-center gap-1 flex-shrink-0">
        <button
          v-if="a.examStatus === 0 || a.examStatus === 2"
          @click="exam(a, 1)"
          class="px-3 h-8 rounded border border-green-200 text-green-600 hover:bg-green-50 text-xs"
        >
          通过
        </button>
        <button
          v-if="a.examStatus === 0 || a.examStatus === 2"
          @click="exam(a, 2)"
          class="px-3 h-8 rounded border border-amber-200 text-amber-600 hover:bg-amber-50 text-xs"
        >
          驳回
        </button>
        <button
          @click="move(idx, -1)"
          :disabled="idx === 0"
          class="w-8 h-8 rounded border border-neutral-300 text-neutral-500 hover:text-primary hover:border-primary disabled:opacity-40"
          title="上移"
        >
          <i class="fa fa-arrow-up"></i>
        </button>
        <button
          @click="move(idx, 1)"
          :disabled="idx === list.length - 1"
          class="w-8 h-8 rounded border border-neutral-300 text-neutral-500 hover:text-primary hover:border-primary disabled:opacity-40"
          title="下移"
        >
          <i class="fa fa-arrow-down"></i>
        </button>
        <button
          @click="toggleStatus(a)"
          class="px-3 h-8 rounded border border-neutral-300 text-neutral-600 hover:border-primary hover:text-primary text-xs"
        >
          {{ a.status === 1 ? "停用" : "启用" }}
        </button>
        <button
          @click="openEdit(a)"
          class="px-3 h-8 rounded border border-neutral-300 text-neutral-600 hover:border-primary hover:text-primary text-xs"
        >
          编辑
        </button>
        <button
          @click="remove(a)"
          class="px-3 h-8 rounded border border-red-200 text-red-500 hover:bg-red-50 text-xs"
        >
          删除
        </button>
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <div
      v-if="editing"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      @click.self="editing = false"
    >
      <div
        class="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div
          class="px-6 py-4 border-b border-neutral-100 flex items-center justify-between sticky top-0 bg-white"
        >
          <h3 class="font-bold">{{ form.id ? "编辑智能体" : "新增智能体" }}</h3>
          <button @click="editing = false" class="text-neutral-400 hover:text-neutral-600">
            <i class="fa fa-close"></i>
          </button>
        </div>

        <div class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1"
              >名称 <span class="text-red-500">*</span></label
            >
            <input
              v-model="form.name"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              placeholder="如：作文批改助手"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">头像地址</label>
            <input
              v-model="form.avatar"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              placeholder="留空则显示名称首字"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">简介</label>
            <input
              v-model="form.intro"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              placeholder="一句话说明这个智能体能做什么"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">标签</label>
            <input
              v-model="form.tags"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              placeholder="逗号分隔，如：语文,备课,作文"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">接入平台</label>
            <select
              v-model="form.platform"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
            >
              <option value="coze">扣子（国内版 api.coze.cn）</option>
              <option value="openai">OpenAI 兼容协议（workbuddy 等）</option>
            </select>
          </div>

          <div v-if="form.platform === 'openai'">
            <label class="block text-sm font-medium mb-1"
              >接口地址 <span class="text-red-500">*</span></label
            >
            <input
              v-model="form.apiBase"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              placeholder="如 https://your-workbuddy.com/v1"
            />
            <p class="text-xs text-neutral-400 mt-1">
              后端会自动拼接 /chat/completions
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">{{
              form.platform === "openai" ? "模型名" : "Bot ID"
            }}</label>
            <input
              v-model="form.botId"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              :placeholder="
                form.platform === 'openai' ? '如 gpt-4o' : '扣子智能体的 bot_id'
              "
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">API 密钥</label>
            <input
              v-model="form.apiToken"
              type="password"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              placeholder="扣子个人访问令牌 / 平台 API Key"
            />
            <p class="text-xs text-neutral-400 mt-1">
              仅保存在服务端，不会下发给任何客户端
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">开场白</label>
            <textarea
              v-model="form.welcome"
              rows="2"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              placeholder="进入对话时智能体说的第一句话"
            ></textarea>
            <p class="text-xs text-neutral-400 mt-1">
              站内代理模式生效；网页嵌入模式的开场白请在扣子平台「智能体编排」里配置
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">对话模式</label>
            <select
              v-model="form.chatMode"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
            >
              <option value="embed">扣子网页嵌入（推荐：开场白 / 快捷提问 / 文件上传）</option>
              <option value="api">站内代理（仅文本对话，密钥不出服务端）</option>
            </select>
            <p class="text-xs text-neutral-400 mt-1">
              嵌入模式需先在扣子平台把智能体发布到「Chat SDK / 网页」渠道，
              并把本站域名加入允许访问的域名白名单
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">快捷提问</label>
            <textarea
              v-model="form.suggestedQuestions"
              rows="3"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              placeholder="每行一条，如：&#10;帮我总结文档要点&#10;提取里面的关键数据&#10;帮我写摘要"
            ></textarea>
            <p class="text-xs text-neutral-400 mt-1">
              站内代理模式生效；网页嵌入模式的推荐问题请在扣子平台配置（SDK 不支持传参）
            </p>
          </div>

          <label
            class="flex items-start gap-2 p-3 rounded-lg border border-neutral-200 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="Number(form.uploadable) === 1"
              @change="form.uploadable = $event.target.checked ? 1 : 0"
              class="mt-0.5"
            />
            <span class="text-sm">
              允许上传文件
              <span class="block text-xs text-neutral-400 mt-0.5">
                开启后输入框会出现上传按钮，可发 PDF / Word 等文档给智能体；仅嵌入模式生效
              </span>
            </span>
          </label>

          <div class="flex items-center gap-4">
            <div class="flex-1">
              <label class="block text-sm font-medium mb-1">排序</label>
              <input
                v-model.number="form.sortNum"
                type="number"
                class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              />
            </div>
            <div class="flex-1">
              <label class="block text-sm font-medium mb-1">状态</label>
              <select
                v-model.number="form.status"
                class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              >
                <option :value="1">启用</option>
                <option :value="0">停用</option>
              </select>
            </div>
          </div>

          <!-- 知识库：导入文本 → 切块 → 站内对话时自动检索注入 -->
          <div v-if="form.id" class="border-t border-neutral-100 pt-4">
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium">
                知识库
                <span class="text-xs text-neutral-400 font-normal">
                  （站内对话时检索前几段注入提问）
                </span>
              </label>
              <label
                class="px-3 py-1.5 rounded-lg bg-primary text-white text-xs cursor-pointer hover:bg-primary/90"
              >
                <i class="fa fa-upload mr-1"></i>上传文档
                <input
                  type="file"
                  class="hidden"
                  accept=".txt,.md,.csv,.json,.html,.htm"
                  @change="uploadKb"
                />
              </label>
            </div>

            <div v-if="kbLoading" class="text-xs text-neutral-400 py-2">
              <i class="fa fa-spinner fa-spin mr-1"></i>加载中…
            </div>
            <div
              v-else-if="!kbDocs.length"
              class="text-xs text-neutral-400 py-2"
            >
              还没有文档，支持 txt / md / csv / json / html（≤5MB），导入后自动切块
            </div>
            <ul v-else class="space-y-1 max-h-40 overflow-y-auto">
              <li
                v-for="d in kbDocs"
                :key="d.id"
                class="flex items-center gap-2 text-xs bg-neutral-50 rounded-lg px-3 py-2"
              >
                <i class="fa fa-file-text-o text-neutral-400"></i>
                <span class="flex-1 truncate">{{ d.name }}</span>
                <span class="text-neutral-400 whitespace-nowrap"
                  >{{ d.chunks }} 段 · {{ fmtKbSize(d.size) }}</span
                >
                <button
                  class="text-red-500 hover:underline whitespace-nowrap"
                  @click="removeKb(d)"
                >
                  删除
                </button>
              </li>
            </ul>

            <div class="flex items-center gap-2 mt-3">
              <input
                v-model="kbQuery"
                class="flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-xs"
                placeholder="输入一句话测试检索命中，如：风险评估要求"
                @keydown.enter="searchKb"
              />
              <button
                class="px-3 py-2 rounded-lg border border-neutral-300 text-xs text-neutral-600 hover:border-primary hover:text-primary disabled:opacity-60"
                :disabled="kbSearching"
                @click="searchKb"
              >
                测试检索
              </button>
            </div>
            <div
              v-if="kbHits.length"
              class="mt-2 space-y-1 max-h-40 overflow-y-auto"
            >
              <p
                v-for="(h, i) in kbHits"
                :key="i"
                class="text-[11px] leading-relaxed text-neutral-700 bg-amber-50 border border-amber-100 rounded-lg p-2 whitespace-pre-wrap"
              >
                {{ h }}
              </p>
            </div>
            <p v-else-if="kbSearched" class="text-xs text-neutral-400 mt-2">
              没有命中，换个说法试试
            </p>
          </div>
        </div>

        <div
          class="px-6 py-4 border-t border-neutral-100 flex justify-end gap-2 sticky bottom-0 bg-white"
        >
          <button
            @click="editing = false"
            class="px-4 py-2 border border-neutral-300 rounded-lg text-sm"
          >
            取消
          </button>
          <button
            @click="save"
            :disabled="saving"
            class="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-60"
          >
            {{ saving ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";

export default {
  name: "AgentManager",
  data() {
    return {
      list: [],
      loading: false,
      editing: false,
      saving: false,
      form: this.emptyForm(),
      // 知识库（仅编辑已有智能体时可管理）
      kbDocs: [],
      kbLoading: false,
      kbQuery: "",
      kbHits: [],
      kbSearched: false,
      kbSearching: false,
    };
  },
  created() {
    this.getList();
  },
  methods: {
    emptyForm() {
      return {
        id: "",
        name: "",
        avatar: "",
        intro: "",
        tags: "",
        platform: "coze",
        apiBase: "",
        botId: "",
        apiToken: "",
        welcome: "",
        suggestedQuestions: "",
        uploadable: 1,
        chatMode: "embed",
        sortNum: 0,
        status: 1,
      };
    },
    platformName(p) {
      return p === "openai" ? "OpenAI 兼容" : "扣子";
    },
    maskToken(t) {
      if (!t) return "";
      return t.length > 8 ? t.substring(0, 6) + "****" : "****";
    },
    examText(a) {
      if (a.examStatus === 0) return "待审核";
      if (a.examStatus === 2) return "已驳回";
      return "已通过";
    },
    examClass(a) {
      if (a.examStatus === 0) return "bg-amber-50 text-amber-600";
      if (a.examStatus === 2) return "bg-red-50 text-red-500";
      return "bg-green-50 text-green-700";
    },
    /** 审核：act 1 通过 / 2 驳回（驳回必须填反馈） */
    async exam(a, act) {
      let info = "";
      try {
        if (act === 2) {
          const { value } = await ElMessageBox.prompt(
            "请写出需要修改的地方，作者会收到这条反馈",
            "驳回：" + a.name,
            {
              confirmButtonText: "确定驳回",
              cancelButtonText: "取消",
              inputType: "textarea",
              inputValidator: (v) => (v && v.trim() ? true : "请填写反馈建议"),
            }
          );
          info = (value || "").trim();
        } else {
          const { value } = await ElMessageBox.prompt(
            "可以补充通过意见（可留空）",
            "通过：" + a.name,
            { confirmButtonText: "通过", cancelButtonText: "取消" }
          );
          info = (value || "").trim();
        }
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "agent/admin/exam",
          method: "POST",
          data: {
            data: await this.$aesEncrypt({ id: a.id, examStatus: act, examInfo: info }),
          },
        });
        if (res.code === 200) {
          ElMessage.success(act === 1 ? "已通过并启用" : "已驳回并通知作者");
          this.getList();
        } else {
          ElMessage.error(res.msg || "操作失败");
        }
      } catch (e) {
        ElMessage.error("操作失败");
      }
    },
    getList() {
      this.loading = true;
      this.$axios({ url: "agent/admin/list", method: "POST" })
        .then((res) => {
          if (res.code === 200) {
            this.list = res.data || [];
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    /** 知识库：拉取当前智能体的文档列表 */
    loadKb() {
      if (!this.form.id) {
        this.kbDocs = [];
        return;
      }
      this.kbLoading = true;
      this.$axios({ url: "agent/kb/list", params: { agentId: this.form.id } })
        .then((res) => {
          if (res.code === 200 && res.data) {
            this.kbDocs = res.data.list || [];
          }
        })
        .catch(() => {})
        .finally(() => {
          this.kbLoading = false;
        });
    },
    /** 上传文本文件到知识库（multipart） */
    uploadKb(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      if (!this.form.id) {
        ElMessage.warning("请先保存智能体，再上传知识库");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        ElMessage.warning("文件请控制在 5MB 以内");
        return;
      }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("agentId", this.form.id);
      this.kbLoading = true;
      this.$axios({
        url: "agent/kb/upload",
        method: "POST",
        data: fd,
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((res) => {
          if (res.code === 200) {
            ElMessage.success(
              "已导入 " + ((res.data && res.data.chunks) || 0) + " 段"
            );
            this.loadKb();
          } else {
            ElMessage.error(res.msg || "导入失败");
          }
        })
        .catch(() => {})
        .finally(() => {
          this.kbLoading = false;
        });
    },
    removeKb(d) {
      ElMessageBox.confirm(
        `删除文档「${d.name}」及其全部切片？`,
        "删除知识库文档",
        { confirmButtonText: "删除", cancelButtonText: "取消", type: "warning" }
      )
        .then(async () => {
          const data = await this.$aesEncrypt({ id: d.id });
          const res = await this.$axios({
            url: "agent/kb/remove",
            method: "POST",
            data: { data },
          });
          if (res.code === 200) {
            ElMessage.success("已删除");
            this.loadKb();
          }
        })
        .catch(() => {});
    },
    /** 测试检索：看看一句话会命中哪些片段 */
    searchKb() {
      if (!this.kbQuery.trim()) return;
      this.kbSearching = true;
      this.kbSearched = false;
      this.$axios({
        url: "agent/kb/search",
        params: { agentId: this.form.id, q: this.kbQuery, top: 3 },
      })
        .then((res) => {
          this.kbHits = (res.data && res.data.hits) || [];
          this.kbSearched = true;
        })
        .catch(() => {})
        .finally(() => {
          this.kbSearching = false;
        });
    },
    fmtKbSize(n) {
      const size = Number(n) || 0;
      if (size < 1024) return size + " 字";
      return (size / 1024).toFixed(1) + "k 字";
    },
    openEdit(agent) {
      this.form = agent
        ? { ...this.emptyForm(), ...agent }
        : this.emptyForm();
      if (!this.form.platform) this.form.platform = "coze";
      if (this.form.platform === "openai") {
        this.form.chatMode = "api";
      } else if (!this.form.chatMode) {
        this.form.chatMode = "embed";
      }
      if (this.form.uploadable === null || this.form.uploadable === undefined) {
        this.form.uploadable = 0;
      }
      this.kbDocs = [];
      this.kbHits = [];
      this.kbSearched = false;
      this.kbQuery = "";
      this.loadKb();
      this.editing = true;
    },
    async save() {
      if (!this.form.name || !this.form.name.trim()) {
        ElMessage.warning("请填写智能体名称");
        return;
      }
      if (this.form.platform === "openai" && !this.form.apiBase) {
        ElMessage.warning("OpenAI 兼容平台需要填写接口地址");
        return;
      }
      this.saving = true;
      try {
        const data = await this.$aesEncrypt(this.form);
        const res = await this.$axios({
          url: "agent",
          method: "PUT",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success("保存成功");
          this.editing = false;
          this.getList();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败");
      } finally {
        this.saving = false;
      }
    },
    async toggleStatus(a) {
      const next = a.status === 1 ? 0 : 1;
      try {
        const data = await this.$aesEncrypt({ ...a, status: next });
        await this.$axios({ url: "agent", method: "PUT", data: { data } });
        this.getList();
      } catch (e) {
        ElMessage.error("操作失败");
      }
    },
    async move(idx, dir) {
      const target = idx + dir;
      if (target < 0 || target >= this.list.length) return;
      const cur = this.list[idx];
      const other = this.list[target];
      const curSort = cur.sortNum || 0;
      const otherSort = other.sortNum || 0;
      try {
        const d1 = await this.$aesEncrypt({ ...cur, sortNum: otherSort });
        await this.$axios({ url: "agent", method: "PUT", data: { data: d1 } });
        const d2 = await this.$aesEncrypt({ ...other, sortNum: curSort });
        await this.$axios({ url: "agent", method: "PUT", data: { data: d2 } });
        this.getList();
      } catch (e) {
        ElMessage.error("排序失败");
      }
    },
    async remove(a) {
      try {
        await ElMessageBox.confirm(
          "确定删除智能体「" + a.name + "」？",
          "删除确认",
          { confirmButtonText: "删除", cancelButtonText: "取消", type: "warning" }
        );
      } catch (e) {
        return;
      }
      try {
        await this.$axios({ url: "agent/" + a.id, method: "DELETE" });
        ElMessage.success("删除成功");
        this.getList();
      } catch (e) {
        ElMessage.error("删除失败");
      }
    },
  },
};
</script>
